import React from "react";
import {
  Card,
  Spin,
  Row,
  Col,
  Form,
  Input,
  Button,
  notification,
} from "antd";
import PropTypes from "prop-types";
import Search from "antd/es/input/Search";

import { AutoCompleteCity } from "components/autocomplete/autocomplete-city";
import { AutoCompleteCountry } from "components/autocomplete/autocomplete-country";
import { STATES_DATA } from "utils/static-data";
import useHttpGeo from "hooks/use-http-geo";

const FormAddress = (props) => {
  const {
    loading,
    form,
    title,
    receiver,
    note,
    card,
    fullWidth,
    compact = false,
    required = true,
    country = false,
    prefix = [],
  } = props;

  const httpGeo = useHttpGeo();
  const [disableCep, setDisableCep] = React.useState(true);
  const addressPath = React.useMemo(() => [...prefix, "address"], [prefix]);

  const getAddress = () => {
    return form.getFieldValue(addressPath) || {};
  };

  const setAddressField = (field, value) => {
    form.setFields([
      {
        name: [...addressPath, field],
        value,
      },
    ]);
  };

  const onSelectedCity = (e) => {
    setAddressField("state", e ? STATES_DATA[e.state] : null);
  };

  async function getAdressInfo() {
    const zipcode = form.getFieldValue([...addressPath, "zipcode"]);

    try {
      const data = await httpGeo.getCep(zipcode);

      form.setFields([
        { name: [...addressPath, "address"], value: data.logradouro },
        { name: [...addressPath, "neighborhood"], value: data.bairro },
        { name: [...addressPath, "city"], value: data.localidade },
        { name: [...addressPath, "state"], value: data.uf },
      ]);
    } catch (err) {
      notification.error({
        message: err.message,
      });
    }
  }

  const Address = (
    <Row gutter={24}>
      <Col style={{ width: "100%" }}>
        <Form.Item
          hasFeedback
          name={[...addressPath, "zipcode"]}
          rules={[
            { required: required, message: "Insira o CEP" },
            {
              pattern: /^\d{5}-\d{3}$|^\d{8}$|^\d{2}\.\d{3}-\d{3}$/,
              message: "Ex: 00.000-000 , 00000-000 ou 00000000",
            },
          ]}
          label="CEP"
          style={{ width: "100%" }}
        >
          <Search
            maxLength={10}
            placeholder="00.000-000"
            autoComplete="off"
            enterButton={
              <Button type="primary" disabled={disableCep}>
                Consultar CEP
              </Button>
            }
            onSearch={getAdressInfo}
            loading={httpGeo.loading}
            onChange={(event) => {
              const value = event.target.value;
              if (/^\d{5}-\d{3}$|^\d{8}$|^\d{2}\.\d{3}-\d{3}$/.test(value)) {
                setDisableCep(false);
              } else {
                setDisableCep(true);
              }
            }}
          />
        </Form.Item>
      </Col>
      <Col
        {...(fullWidth
          ? country
            ? { xl: 4, lg: 8 }
            : { xl: 4, lg: 12 }
          : country
          ? { xl: 14, lg: 8, md: 20 }
          : { xl: 18, lg: 12, md: 24 })}
      >
        <Form.Item
          label="Cidade"
          hasFeedback
          name={[...addressPath, "city"]}
          rules={[
            { required: required, message: "Insira a cidade" },
            { max: 100, message: "Campo não deve ultrapassar 100 caracteres" },
          ]}
        >
          <AutoCompleteCity onSelectedItem={onSelectedCity} />
        </Form.Item>
      </Col>
      {country && (
        <Col md={6}>
          <Form.Item
            label="País"
            hasFeedback
            name={[...addressPath, "country"]}
            initialValue={"BRA"}
            rules={[{ required: required, message: "Selecione um país" }]}
          >
            <AutoCompleteCountry
              onSelectedItem={(item) => {
                setAddressField("country", item?.code || null);
              }}
            />
          </Form.Item>
        </Col>
      )}
      <Col
        {...(fullWidth
          ? { xl: 2 }
          : country
          ? { xl: 4, lg: 10, md: 22 }
          : { xl: 6, lg: 12, md: 24 })}
      >
        <Form.Item label="Estado" name={[...addressPath, "state"]}>
          <Input disabled autoComplete="off" />
        </Form.Item>
      </Col>
      <Col {...(fullWidth ? { xl: 5 } : { xl: 17, lg: 12, md: 24 })}>
        <Form.Item
          label="Rua"
          hasFeedback
          name={[...addressPath, "address"]}
          rules={[
            { required: required, message: "Insira a rua" },
            { max: 100, message: "Campo não deve ultrapassar 100 caracteres" },
          ]}
        >
          <Input maxLength={100} autoComplete="off" />
        </Form.Item>
      </Col>
      <Col {...(fullWidth ? { xl: 2 } : { xl: 7, lg: 12, md: 24 })}>
        <Form.Item
          label="Número"
          hasFeedback
          name={[...addressPath, "number"]}
          rules={[{ required: required, message: "Insira o número" }]}
        >
          <Input autoComplete="off" />
        </Form.Item>
      </Col>
      <Col {...(fullWidth ? { xl: 4 } : { xl: 12, lg: 12, md: 24 })}>
        <Form.Item
          label="Complemento"
          name={[...addressPath, "complement"]}
          rules={[{ max: 100, message: "Campo não deve ultrapassar 100 caracteres" }]}
        >
          <Input maxLength={100} autoComplete="off" />
        </Form.Item>
      </Col>
      <Col {...(fullWidth ? { xl: 4 } : { xl: 12, lg: 12, md: 24 })}>
        <Form.Item
          label="Bairro"
          hasFeedback
          name={[...addressPath, "neighborhood"]}
          rules={[
            { required: required, message: "Insira o bairro" },
            { max: 100, message: "Campo não deve ultrapassar 100 caracteres" },
          ]}
        >
          <Input maxLength={100} autoComplete="off" />
        </Form.Item>
      </Col>
      {receiver && (
        <Col {...(fullWidth ? {} : { md: 24 })}>
          <Form.Item
            label="Destinatário"
            name={[...prefix, "receiver"]}
            hasFeedback
            rules={[{ required: required, message: "Insira o destinatário" }]}
          >
            <Input autoComplete="off" />
          </Form.Item>
        </Col>
      )}
      {note && (
        <Col {...(fullWidth ? {} : { md: 24 })}>
          <Form.Item
            label="Observação"
            hasFeedback
            name={[...prefix, "note"]}
            rules={[{ required: required, message: "Insira uma observação" }]}
          >
            <Input autoComplete="off" />
          </Form.Item>
        </Col>
      )}
    </Row>
  );

  if (card) {
    return (
      <Card title={title} style={compact ? { height: "100%" } : {}}>
        <Spin spinning={loading}>{Address}</Spin>
      </Card>
    );
  }
  return Address;
};

FormAddress.defaultProps = {
  loading: false,
  title: "Endereço",
  receiver: false,
  note: false,
  card: true,
  fullWidth: false,
  required: true,
  prefix: [],
};

FormAddress.propTypes = {
  loading: PropTypes.bool,
  form: PropTypes.any.isRequired,
  title: PropTypes.string,
  receiver: PropTypes.bool,
  note: PropTypes.bool,
  card: PropTypes.bool,
  fullWidth: PropTypes.bool,
  required: PropTypes.bool,
  compact: PropTypes.bool,
  country: PropTypes.bool,
  prefix: PropTypes.array,
};

export { FormAddress };